import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ForceReplaceNotice, RenameChoice, RenamePrompt } from "./schema";

interface RenamePromptDialogProps {
  prompts: RenamePrompt[];
  notices: ForceReplaceNotice[];
  onConfirm: (choices: Record<string, RenameChoice>) => void;
  onCancel: () => void;
}

export function RenamePromptDialog({
  prompts,
  notices,
  onConfirm,
  onCancel,
}: RenamePromptDialogProps) {
  /** Default every prompt to "rename" — the less destructive choice. */
  const [choices, setChoices] = useState<Record<string, RenameChoice>>(() =>
    Object.fromEntries(prompts.map((p) => [p.id, "rename" as RenameChoice])),
  );

  const hasPrompts = prompts.length > 0;
  const hasNotices = notices.length > 0;

  const title = hasPrompts
    ? "Rename or remove and create new?"
    : "Items will be replaced";

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onCancel();
        }
      }}
    >
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] gap-0 p-0 flex flex-col">
        <DialogHeader className="space-y-3 px-6 pt-6">
          <DialogTitle className="text-lg">{title}</DialogTitle>
          {hasPrompts && (
            <p className="text-base text-gray-600 leading-relaxed">
              You changed the name of the following
              {prompts.length === 1 ? " item" : " items"}. Is each one a rename
              of the existing object, or a different object that should replace
              the old one?
            </p>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
          {hasPrompts && (
            <div className="rounded-md bg-gray-50 border border-gray-100 px-3 py-2.5 text-sm text-gray-600 leading-relaxed">
              <strong>Rename</strong> keeps any in-flight candidate progress
              tied to the same object. <strong>Remove and create new</strong>{" "}
              deletes the old object and creates a fresh one — in-flight
              candidates will need to redo this step.
            </div>
          )}

          {hasNotices && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-3 space-y-2">
              <div className="text-sm font-semibold text-amber-900">
                The following {notices.length === 1 ? "item" : "items"} will be
                replaced
              </div>
              <p className="text-sm text-amber-800 leading-relaxed">
                Changing an item's type or assignee creates a new object.
                In-flight candidates will need to redo this step.
              </p>
              <ul className="text-sm text-amber-900 space-y-1 pl-1">
                {notices.map((n) => (
                  <li key={n.id}>
                    <span className="font-medium">
                      &ldquo;{n.itemName}&rdquo;
                    </span>{" "}
                    <span className="text-amber-700">
                      (in {n.stageName}) — {n.reason}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasPrompts && (
            <div className="space-y-3">
              {prompts.map((prompt) => {
                const label =
                  prompt.kind === "stage"
                    ? `Stage "${prompt.oldName}" → "${prompt.newName}"`
                    : `Item "${prompt.oldName}" → "${prompt.newName}" (in stage "${prompt.stageName}")`;
                const choice = choices[prompt.id];
                return (
                  <div
                    key={prompt.id}
                    className="border border-gray-200 rounded-lg p-3 space-y-2.5"
                  >
                    <div className="text-base font-medium text-gray-800">
                      {label}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setChoices((prev) => ({
                            ...prev,
                            [prompt.id]: "rename",
                          }))
                        }
                        className={`flex-1 text-sm px-3 py-2 rounded-md border ${
                          choice === "rename"
                            ? "bg-blue-50 border-blue-300 text-primary font-medium"
                            : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setChoices((prev) => ({
                            ...prev,
                            [prompt.id]: "recreate",
                          }))
                        }
                        className={`flex-1 text-sm px-3 py-2 rounded-md border ${
                          choice === "recreate"
                            ? "bg-amber-50 border-amber-300 text-amber-800 font-medium"
                            : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        Remove and create new
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 pb-6 pt-2 border-t border-gray-100">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={() => onConfirm(choices)}>
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
